import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, buildMediaUrl } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import ArticleCard from "../components/ArticleCard";
import { ArrowUpRight, AlertTriangle, Eye } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/dca8d16d05bfa008716cefc7e7a5bd6962ad52c48f3338d882f3b7ecd8af7337.png";

const VIEWS_LABEL = { ar: "مشاهدة", en: "views", fr: "vues", he: "צפיות", de: "Aufrufe" };
function fmtViews(n, lang) {
  const num = typeof n === "number" ? n : 0;
  const display = num >= 1000 ? `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}k` : `${num}`;
  return `${display} ${VIEWS_LABEL[lang] || VIEWS_LABEL.en}`;
}

const CATEGORY_FALLBACK_IMG = {
  war_crime: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/07d06b03700bad39a92b3777f5a8f0215faa452d7438dcc85358c0f56db0d3eb.png",
  terror_crime: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/af3383f01b902e9c59eacfbaee9a4c68f6f87ee413498a382bce77842fa0897b.png",
  monitoring: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/af3383f01b902e9c59eacfbaee9a4c68f6f87ee413498a382bce77842fa0897b.png",
  study: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/d674fcde06ed8375649c122b40877b2edc71287780b0b40fbaaf78fac4093ff5.png",
  director_message: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/d674fcde06ed8375649c122b40877b2edc71287780b0b40fbaaf78fac4093ff5.png",
  analysis: "https://images.unsplash.com/photo-1768213022263-0414dc145dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  news: "https://images.unsplash.com/photo-1758138258458-64ee0fbdef4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  publication: "https://images.unsplash.com/photo-1598420563273-df02d9118be7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

function FeatureCard({ article, index, pickArticle, t, lang }) {
  const block = pickArticle(article);
  const img = article.featured_image
    ? buildMediaUrl(article.featured_image)
    : CATEGORY_FALLBACK_IMG[article.category] || CATEGORY_FALLBACK_IMG.analysis;
  const catLabel = t.categories[article.category] || article.category;
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block tile overflow-hidden"
      data-testid={`feature-card-${article.slug}`}
    >
      <div className="relative aspect-[16/9] bg-slate100 overflow-hidden">
        <img
          src={img}
          alt={block.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <div className="absolute top-4 start-4 inline-flex items-center gap-2">
          <span className="bg-ink text-white text-[10px] uppercase tracking-widest px-2.5 py-1.5 font-semibold">
            #{index}
          </span>
          {article.is_breaking && (
            <span className="bg-crimson text-white inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1.5">
              <AlertTriangle className="w-3 h-3" /> Breaking
            </span>
          )}
        </div>
        <div className="absolute top-4 end-4">
          <span
            className="bg-white/95 backdrop-blur-sm text-ink inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1.5 border border-slate200"
            data-testid={`feature-views-${article.slug}`}
          >
            <Eye className="w-3 h-3" /> {fmtViews(article.views, lang)}
          </span>
        </div>
      </div>
      <div className="p-7 lg:p-8">
        <div className="label-eyebrow text-ink mb-3">{catLabel}</div>
        <h3 className="heading-serif text-2xl lg:text-[28px] text-ink leading-[1.2] mb-3 group-hover:opacity-80 transition-opacity">
          {block.title}
        </h3>
        <p className="text-[14px] text-slate600 leading-relaxed line-clamp-3 mb-5">
          {block.summary}
        </p>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink font-medium">
          {t.home.read_more} <ArrowUpRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const { t, pickArticle, lang } = useLang();
  const [reports, setReports] = useState([]);
  const [news, setNews] = useState([]);
  const [analysis, setAnalysis] = useState([]);
  const [director, setDirector] = useState(null);
  const [breaking, setBreaking] = useState([]);
  const [latest7, setLatest7] = useState([]);

  useEffect(() => {
    (async () => {
      const [r, n, a, d, b, l7] = await Promise.all([
        api.get("/articles", { params: { category: "study", limit: 6 } }),
        api.get("/articles", { params: { category: "news", limit: 5 } }),
        api.get("/articles", { params: { category: "analysis", limit: 4 } }),
        api.get("/articles", { params: { category: "director_message", limit: 1 } }),
        api.get("/articles", { params: { is_breaking: true, limit: 3 } }),
        api.get("/articles", { params: { limit: 7, status: "published" } }),
      ]);
      setReports(r.data.items || []);
      setNews(n.data.items || []);
      setAnalysis(a.data.items || []);
      setDirector((d.data.items || [])[0] || null);
      setBreaking(b.data.items || []);
      setLatest7(l7.data.items || []);
    })().catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-slate200 bg-paper text-ink"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 opacity-15 grain"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-paper via-paper/95 to-paper/70" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="label-eyebrow text-slate600 mb-6">{t.home.eyebrow}</div>
            <h1 className="heading-serif text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] text-ink mb-7 max-w-4xl">
              {t.home.hero_title}
            </h1>
            <p className="text-[16px] text-slate700 leading-relaxed max-w-2xl mb-10">
              {t.home.hero_sub}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/studies"
                data-testid="hero-cta-studies"
                className="inline-flex items-center gap-2 bg-ink text-white h-11 px-5 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 transition-colors"
              >
                {t.home.cta_studies} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/war-crimes"
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-2 border border-ink/40 text-ink h-11 px-5 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink hover:text-white transition-colors"
              >
                {t.home.cta_explore}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex flex-col justify-end">
            <div className="border border-slate300 p-6 backdrop-blur-sm bg-white/60">
              <div className="label-eyebrow text-slate600 mb-3">SCOPE</div>
              <ul className="text-[13px] text-slate700 leading-relaxed space-y-2">
                <li>· Counter-terrorism research</li>
                <li>· War-crimes documentation</li>
                <li>· Strategic & political analysis</li>
                <li>· Field monitoring & geo-intel</li>
                <li>· Multilingual research output (AR · EN · FR)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BREAKING TICKER */}
      {breaking.length > 0 && (
        <section className="border-b border-slate200 bg-white" data-testid="breaking-ticker">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-3 flex items-center gap-4 overflow-x-auto">
            <span className="inline-flex items-center gap-1 text-crimson text-[10px] uppercase tracking-widest font-semibold shrink-0">
              <AlertTriangle className="w-3 h-3" /> Breaking
            </span>
            <div className="flex gap-6 text-[13px] text-slate700">
              {breaking.map((b) => {
                const block = pickArticle(b);
                return (
                  <Link
                    key={b.id}
                    to={`/article/${b.slug}`}
                    className="link-underline shrink-0 hover:text-ink"
                    data-testid={`breaking-${b.slug}`}
                  >
                    {block.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* LATEST PUBLICATIONS — 7 most recent across all categories */}
      {latest7.length > 0 && (
        <section
          className="border-b border-slate200 bg-white"
          data-testid="latest-publications"
        >
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
            <div className="flex items-end justify-between mb-10 gap-6">
              <div>
                <div className="label-eyebrow text-ink mb-2">00 / Featured</div>
                <h2 className="heading-serif text-3xl lg:text-4xl text-ink leading-tight">
                  {t.home.latest_publications}
                </h2>
                <p className="text-[14px] text-slate600 mt-3 max-w-2xl">
                  {t.home.latest_publications_sub}
                </p>
              </div>
            </div>

            {/* Top row: two large feature cards side by side */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {latest7.slice(0, 2).map((a, idx) => (
                <FeatureCard key={a.id} article={a} index={idx + 1} pickArticle={pickArticle} t={t} lang={lang} />
              ))}
            </div>

            {/* Second row: remaining 5 in a grid */}
            {latest7.length > 2 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {latest7.slice(2, 7).map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* LATEST REPORTS */}
      {reports.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16" data-testid="latest-reports">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-eyebrow text-ink mb-2">01 / Reports</div>
              <h2 className="heading-serif text-3xl lg:text-4xl text-ink">{t.home.latest_reports}</h2>
            </div>
            <Link to="/studies" className="link-underline text-[12px] uppercase tracking-widest text-ink" data-testid="all-studies-link">
              {t.home.view_all}
            </Link>
          </div>
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <ArticleCard article={reports[0]} variant="lead" />
            </div>
            <div className="lg:col-span-4 grid gap-5">
              {reports.slice(1, 4).map((a) => (
                <ArticleCard key={a.id} article={a} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DIRECTOR + ANALYSIS split */}
      <section className="border-t border-slate200 bg-slate50" data-testid="director-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
          {director && (
            <Link
              to={`/article/${director.slug}`}
              className="lg:col-span-7 tile p-8 lg:p-12 group"
              data-testid="director-card"
            >
              <div className="label-eyebrow text-ink mb-4">02 / {t.home.director_block}</div>
              <h2 className="heading-serif text-3xl lg:text-[40px] leading-[1.1] text-ink mb-5 group-hover:opacity-80 transition-opacity">
                {pickArticle(director).title}
              </h2>
              <p className="text-[15px] text-slate600 leading-relaxed mb-6 line-clamp-5">
                {pickArticle(director).summary}
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-slate200">
                <div className="w-10 h-10 bg-ink text-white grid place-items-center text-xs font-bold">D</div>
                <div>
                  <div className="text-[13px] text-ink font-semibold">Director, MESC-CE</div>
                  <div className="text-[11px] text-slate500 uppercase tracking-wider">Weekly Editorial</div>
                </div>
              </div>
            </Link>
          )}
          <div className="lg:col-span-5 grid gap-5">
            <div className="label-eyebrow text-ink">03 / {t.home.featured_analysis}</div>
            {analysis.slice(0, 4).map((a) => (
              <ArticleCard key={a.id} article={a} variant="compact" />
            ))}
            {analysis.length === 0 && (
              <div className="text-slate500 text-sm">{t.common.loading}</div>
            )}
          </div>
        </div>
      </section>

      {/* NEWS strip */}
      {news.length > 0 && (
        <section className="border-t border-slate200 bg-white">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16" data-testid="latest-news">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="label-eyebrow text-ink mb-2">04 / {t.home.latest_news}</div>
                <h2 className="heading-serif text-3xl lg:text-4xl text-ink">{t.sections.news_title}</h2>
              </div>
              <Link to="/news" className="link-underline text-[12px] uppercase tracking-widest text-ink">
                {t.home.view_all}
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
              {news.slice(0, 4).map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTIONS GRID */}
      <section className="border-t border-slate200 bg-paper text-ink">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
          <div className="label-eyebrow text-slate600 mb-3">05 / Research Tracks</div>
          <h2 className="heading-serif text-3xl lg:text-5xl text-ink mb-12 max-w-3xl leading-tight">
            Five permanent investigative tracks across the Middle East.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 border-l border-slate300 border-t bg-white">
            {[
              { to: "/war-crimes", title: t.sections.war_crimes_title, sub: t.sections.war_crimes_sub },
              { to: "/terror-organizations", title: t.sections.terror_crimes_title, sub: t.sections.terror_crimes_sub },
              { to: "/monitoring", title: t.sections.monitoring_title, sub: t.sections.monitoring_sub },
              { to: "/studies", title: t.sections.studies_title, sub: t.sections.studies_sub },
              { to: "/analysis", title: t.sections.analysis_title, sub: t.sections.analysis_sub },
            ].map((s, i) => (
              <Link
                key={s.to}
                to={s.to}
                className="border-r border-b border-slate300 p-7 hover:bg-ink hover:text-white transition-colors group"
                data-testid={`track-${i}`}
              >
                <div className="text-[10px] uppercase tracking-widest mb-4 text-slate500 group-hover:text-white/70">0{i + 1}</div>
                <h3 className="heading-serif text-xl mb-3 leading-tight">{s.title}</h3>
                <p className="text-[12.5px] leading-relaxed text-slate600 group-hover:text-white/75 line-clamp-3">{s.sub}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-medium">
                  {t.home.see_archive} <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
